import { useLayoutEffect, useRef, useState } from "react";
import { motion, useReducedMotion } from "motion/react";
import { spring } from "../lib/springs";

/* ============================================================================
   Collapsible region — the content half of beui's Animated Collapsible
   (beui.dev/components/motion/collapsible), hand-ported: no Tailwind here,
   and the trigger usually lives somewhere else (a StatCard's "View more"),
   so this takes `open` from its parent instead of owning a trigger.

     <Collapsible id="detail" open={open} triggerId="detail-trigger">
       …
     </Collapsible>

   The content stays mounted. Its height is measured (and re-measured as it
   changes) and the wrapper springs between 0 and that figure on the slow
   tier — a full-width surface. The wrapper animates real height, not a
   layout transform, so whatever sits below slides down with it instead of
   jumping. The text fades in over the spring and out a little quicker.

   Closed, the region is aria-hidden and inert, so nothing in it can be
   tabbed into; if focus was inside when it closes, it goes back to the
   element `triggerId` names. Reduced motion skips the height spring and
   keeps only the fade.
   ========================================================================== */
const Collapsible = ({
  open,
  id,
  triggerId,
  className = "",
  contentClassName = "",
  children,
}) => {
  const reduce = useReducedMotion() ?? false;
  const outerRef = useRef(null);
  const innerRef = useRef(null);
  const [height, setHeight] = useState(0);

  useLayoutEffect(() => {
    const node = innerRef.current;
    if (!node) return undefined;
    const measure = () => setHeight(node.offsetHeight);
    measure();
    if (typeof ResizeObserver === "undefined") return undefined;
    const observer = new ResizeObserver(measure);
    observer.observe(node);
    return () => observer.disconnect();
  }, []);

  useLayoutEffect(() => {
    if (open || !triggerId) return;
    if (outerRef.current?.contains(document.activeElement)) {
      document.getElementById(triggerId)?.focus({ preventScroll: true });
    }
  }, [open, triggerId]);

  return (
    <motion.div
      ref={outerRef}
      id={id}
      role="region"
      aria-labelledby={triggerId}
      aria-hidden={!open}
      inert={open ? undefined : ""}
      data-state={open ? "open" : "closed"}
      className={("collapsible " + className).trim()}
      initial={false}
      animate={{ height: open ? height : 0 }}
      transition={reduce ? { duration: 0 } : spring.slow}
    >
      <motion.div
        ref={innerRef}
        className={("collapsible-inner " + contentClassName).trim()}
        initial={false}
        animate={{ opacity: open ? 1 : 0 }}
        transition={open ? spring.moderate : spring.moderate.exit}
      >
        {children}
      </motion.div>
    </motion.div>
  );
};

export default Collapsible;
